import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';
import { monitorApi, type TeamTrafficSummary } from '@/lib/api';
import { PageGuide } from '@/components/PageGuide';
import { usePortalSocketEvent } from '@/contexts/PortalSocketContext';
import {
  AlertTriangle, CheckCircle2, Users,
  MessageSquare, RefreshCw, Filter,
} from 'lucide-react';

export const Route = createFileRoute('/_auth/monitor')({
  component: MonitorPage,
});

/* ── Traffic gauge bar ───────────────────────────────── */
function TrafficBar({ percent, isHighTraffic }: { percent: number; isHighTraffic: boolean }) {
  const fill = Math.min(percent, 100);
  const color = isHighTraffic ? '#ef4444' : fill > 70 ? '#f59e0b' : '#10b981';
  return (
    <div style={{ height: 4, borderRadius: 4, background: '#1e293b', overflow: 'hidden', marginTop: 6 }}>
      <div style={{
        height: '100%', width: `${fill}%`, borderRadius: 4,
        background: color, transition: 'width 0.4s ease',
      }} />
    </div>
  );
}

/* ── Team traffic card ───────────────────────────────── */
function TeamCard({
  summary, selected, onClick,
}: {
  summary: TeamTrafficSummary;
  selected: boolean;
  onClick: () => void;
}) {
  const { isHighTraffic, loadPercent, openConversations, threshold, teamName, color, pendingHitl } = summary;

  return (
    <button
      id={`monitor-team-${summary.teamId}`}
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', gap: 6,
        padding: '14px 16px', borderRadius: 12, textAlign: 'left', cursor: 'pointer',
        border: `1px solid ${isHighTraffic ? '#ef444450' : selected ? '#6366f150' : 'var(--border, #2d3148)'}`,
        background: isHighTraffic ? '#ef444408' : selected ? '#6366f108' : 'var(--surface-2, #1e2130)',
        transition: 'all 0.15s', width: '100%',
        boxShadow: isHighTraffic ? '0 0 0 1px #ef444430' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
            background: color ?? '#6366f1',
            boxShadow: isHighTraffic ? `0 0 6px ${color ?? '#ef4444'}` : 'none',
          }} />
          <span style={{ fontWeight: 600, fontSize: 13, color: '#e2e8f0' }}>{teamName}</span>
        </div>
        {isHighTraffic && (
          <span style={{
            fontSize: 10, fontWeight: 700, color: '#ef4444',
            background: '#ef444420', padding: '2px 7px', borderRadius: 8,
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            <AlertTriangle size={9} /> HIGH TRAFFIC
          </span>
        )}
      </div>

      <TrafficBar percent={loadPercent} isHighTraffic={isHighTraffic} />

      <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#64748b' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: isHighTraffic ? '#f87171' : '#94a3b8' }}>
          <MessageSquare size={10} />
          {openConversations} / {threshold} open
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Users size={10} />
          {pendingHitl} pending HITL
        </span>
        <span style={{ marginLeft: 'auto', color: isHighTraffic ? '#f87171' : '#475569' }}>
          {Math.round(loadPercent)}%
        </span>
      </div>
    </button>
  );
}

/* ── Main page ───────────────────────────────────────── */
export default function MonitorPage() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const { data: traffic = [], isLoading: loadingTraffic, refetch: refetchTraffic } = useQuery({
    queryKey: ['monitor', 'traffic'],
    queryFn: monitorApi.traffic,
    refetchInterval: 60_000, // fallback only — socket invalidates on handoff/traffic events
  });

  const { data: conversations = [], isLoading: loadingConvs } = useQuery({
    queryKey: ['monitor', 'conversations', selectedTeamId],
    queryFn: () => monitorApi.conversations(selectedTeamId ? { teamId: selectedTeamId, limit: 50 } : { limit: 50 }),
    refetchInterval: 30_000, // fallback
  });

  // Real-time: invalidate on handoff events from portal socket
  usePortalSocketEvent('counts:update', () => {
    qc.invalidateQueries({ queryKey: ['monitor', 'traffic'] });
    qc.invalidateQueries({ queryKey: ['monitor', 'conversations'] });
  });

  useEffect(() => {
    const interval = setInterval(() => setLastRefresh(new Date()), 10_000);
    return () => clearInterval(interval);
  }, []);

  const highTrafficTeams = traffic.filter(t => t.isHighTraffic);
  const totalOpen = traffic.reduce((sum, t) => sum + t.openConversations, 0);
  const totalPendingHitl = traffic.reduce((sum, t) => sum + t.pendingHitl, 0);

  return (
    <div className="page">
      <PageGuide pageKey="monitor" />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>
            {t('nav.monitor')}
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
            Last updated {lastRefresh.toLocaleTimeString()} · auto-refreshing
          </p>
        </div>
        <button
          onClick={() => { refetchTraffic(); setLastRefresh(new Date()); }}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px',
            borderRadius: 8, border: '1px solid var(--border, #2d3148)',
            background: 'transparent', color: '#94a3b8', cursor: 'pointer', fontSize: 12,
          }}
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        {[
          {
            icon: <MessageSquare size={18} style={{ color: '#6366f1' }} />,
            label: 'Total open conversations',
            value: totalOpen,
            accent: '#6366f1',
          },
          {
            icon: <AlertTriangle size={18} style={{ color: highTrafficTeams.length > 0 ? '#ef4444' : '#64748b' }} />,
            label: 'Teams over threshold',
            value: highTrafficTeams.length,
            accent: highTrafficTeams.length > 0 ? '#ef4444' : '#64748b',
          },
          {
            icon: <Users size={18} style={{ color: '#f59e0b' }} />,
            label: 'Pending HITL decisions',
            value: totalPendingHitl,
            accent: '#f59e0b',
          },
        ].map(stat => (
          <div key={stat.label} style={{
            padding: '16px 18px', borderRadius: 12,
            border: '1px solid var(--border, #2d3148)',
            background: 'var(--surface-2, #1e2130)',
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: `${stat.accent}15`, display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {stat.icon}
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: stat.accent, lineHeight: 1.1 }}>
                {stat.value}
              </div>
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* High traffic banner */}
      {highTrafficTeams.length > 0 && (
        <div style={{
          marginBottom: 20, padding: '12px 16px', borderRadius: 10,
          background: '#ef444410', border: '1px solid #ef444430',
          display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#f87171',
        }}>
          <AlertTriangle size={16} />
          <span>
            <strong>{highTrafficTeams.map(t => t.teamName).join(', ')}</strong>
            {' '}
            {highTrafficTeams.length === 1 ? 'is' : 'are'} over capacity. Consider redistributing workload or adding operators.
          </span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'start' }}>
        {/* ── Team list ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* All teams option */}
          <button
            id="monitor-all-teams"
            onClick={() => setSelectedTeamId(null)}
            style={{
              padding: '10px 14px', borderRadius: 10, textAlign: 'left', cursor: 'pointer',
              border: `1px solid ${!selectedTeamId ? '#6366f150' : 'var(--border, #2d3148)'}`,
              background: !selectedTeamId ? '#6366f110' : 'transparent',
              color: !selectedTeamId ? '#a5b4fc' : '#64748b',
              fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <Filter size={12} /> All Teams
          </button>

          {loadingTraffic && [1,2,3].map(i => (
            <div key={i} style={{ height: 80, borderRadius: 10, background: '#1e2130', opacity: 0.4 }} />
          ))}

          {traffic.map(team => (
            <TeamCard
              key={team.teamId}
              summary={team}
              selected={selectedTeamId === team.teamId}
              onClick={() => setSelectedTeamId(team.teamId === selectedTeamId ? null : team.teamId)}
            />
          ))}

          {!loadingTraffic && traffic.length === 0 && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#475569', fontSize: 12 }}>
              No teams configured yet.
            </div>
          )}
        </div>

        {/* ── Conversation feed ── */}
        <div style={{
          borderRadius: 12, border: '1px solid var(--border, #2d3148)',
          background: 'var(--surface-2, #1e2130)', overflow: 'hidden',
        }}>
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid var(--border, #2d3148)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Live Conversations {selectedTeamId && `· ${traffic.find(t => t.teamId === selectedTeamId)?.teamName}`}
            </span>
            <span style={{ fontSize: 11, color: '#475569' }}>{conversations.length} shown</span>
          </div>

          {loadingConvs && (
            <div style={{ padding: 16 }}>
              {[1,2,3].map(i => <div key={i} style={{ height: 44, borderRadius: 8, background: '#1e293b', marginBottom: 8, opacity: 0.4 }} />)}
            </div>
          )}

          {!loadingConvs && conversations.length === 0 && (
            <div style={{ padding: '48px 0', textAlign: 'center', color: '#475569' }}>
              <CheckCircle2 size={28} style={{ opacity: 0.25, marginBottom: 8 }} />
              <p style={{ margin: 0, fontSize: 13 }}>
                {selectedTeamId ? 'No open conversations in this team.' : 'No active conversations right now.'}
              </p>
            </div>
          )}

          {conversations.map(conv => (
            <div
              key={conv.id}
              style={{
                padding: '12px 16px', borderBottom: '1px solid var(--border, #2d3148)',
                display: 'flex', alignItems: 'center', gap: 12, fontSize: 12,
              }}
            >
              <span style={{
                width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                background: conv.status === 'OPEN' ? '#10b981' : '#f59e0b',
              }} />
              <span style={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: 11 }}>
                {conv.id.slice(0, 12)}…
              </span>
              <span style={{ color: '#64748b' }}>
                {conv.agent?.name ?? 'Unknown agent'}
              </span>
              <span style={{
                marginLeft: 'auto', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600,
                background: conv.status === 'OPEN' ? '#10b98115' : '#f59e0b15',
                color: conv.status === 'OPEN' ? '#34d399' : '#fbbf24',
              }}>
                {conv.status}
              </span>
              <span style={{ color: '#475569', fontSize: 11 }}>
                {conv.channel}
              </span>
              <span style={{ color: '#475569', fontSize: 11 }}>
                {new Date(conv.createdAt).toLocaleTimeString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
